import { useCallback, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { foyerApi } from "../http/foyer";
import { PageHeader } from "./PageHeader";
import {
  formatDuration,
  formatPercent,
  EngagementBreakdown,
  EngagementMetric,
} from "./engagementFormat";
import type { EngagementSummary } from "@shared/types/engagement";

/**
 * Content-engagement dashboard for one premise: which sections, streaming apps,
 * promotions and radio stations guests actually use on the in-room TVs - and,
 * for the ones we can time, how long they hold attention.
 *
 * Everything here is a count or a duration over a UTC-day rollup. There is no
 * per-guest or per-room breakdown by design (a hotel TV sits in a private room,
 * so finer grain would be surveillance rather than product analytics), and the
 * panel says so out loud: that stance is a reason to buy this in Europe, not a
 * gap to apologise for. Reads `GET /api/foyer/premise/:id/engagement`.
 */

const RANGES = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 365, label: "Last 12 months" },
];

export default function EngagementPanel({
  premiseId,
  showCasting = true,
}: {
  premiseId: string;
  /**
   * Whether to report screen sharing. False on a care premise: casting is a
   * hospitality function, those boxes ship with Chromecast removed, and a
   * permanent "Screen shares: 0" reads as a feature they have failed to use
   * rather than one they were never sold.
   */
  showCasting?: boolean;
}) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<EngagementSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await foyerApi.engagement(premiseId, days));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [premiseId, days]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = data?.totals;
  // A premise whose TVs are all on an older bundle reports nothing at all -
  // say so explicitly rather than showing a wall of confident zeros.
  const hasData =
    !!totals &&
    totals.sectionViews +
      totals.appLaunches +
      totals.radioPlays +
      totals.castStarts +
      totals.slotViews >
      0;

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow="TV"
        title="Engagement"
        help="What guests use on the in-room TVs. Aggregate counts only - no guest or room-level tracking."
        actions={
          <>
            <TextField
              select
              size="small"
              label="Range"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              data-testid="engagement-range"
              sx={{ minWidth: 160 }}
            >
              {RANGES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
            {/* A plain link, not a fetch: the browser streams the file and
                carries the session cookie itself, so a year of data never has
                to be materialised in a JS string first. */}
            <Button
              component="a"
              href={foyerApi.engagementCsvUrl(premiseId, days)}
              startIcon={<DownloadOutlinedIcon />}
              variant="outlined"
              size="medium"
              data-testid="engagement-export"
            >
              Export CSV
            </Button>
          </>
        }
      />

      {error && <Alert severity="error">{error}</Alert>}
      {loading && !data && <CircularProgress size={24} />}

      {data && (
        <>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={5} flexWrap="wrap" useFlexGap>
                <EngagementMetric
                  label="Section views"
                  value={data.totals.sectionViews}
                />
                <EngagementMetric
                  label="App launches"
                  value={data.totals.appLaunches}
                />
                <EngagementMetric
                  label="Time in apps"
                  value={formatDuration(data.totals.appSeconds)}
                />
                <EngagementMetric
                  label="Radio plays"
                  value={data.totals.radioPlays}
                />
                {showCasting && (
                  <EngagementMetric
                    label="Screen shares"
                    value={data.totals.castStarts}
                    hint={`${formatDuration(data.totals.castSeconds)} of casting`}
                  />
                )}
                <EngagementMetric
                  label="Input switches"
                  value={data.totals.inputSwitches}
                />
              </Stack>

              {!hasData && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No engagement recorded in this period. Screens report usage
                  from the current TV bundle onwards - a premise whose TVs have
                  not yet updated will stay empty here.
                </Alert>
              )}

              <Divider sx={{ my: 3 }} />

              <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                <EngagementBreakdown
                  title="Sections opened"
                  entries={data.sections}
                  empty="No sections opened yet."
                />
                <EngagementBreakdown
                  title="Radio stations"
                  entries={data.stations}
                  empty="No stations played yet."
                />
                <EngagementBreakdown
                  title="Languages chosen"
                  entries={data.languages}
                  empty="Nobody switched language."
                />
                <EngagementBreakdown
                  title="Information read"
                  entries={data.info}
                  empty="No information sections were read."
                />
              </Stack>
            </CardContent>
          </Card>

          <AppsCard summary={data} />
          <PromotionsCard summary={data} />

          <Typography variant="caption" color="text.secondary">
            Counts are aggregated per property per UTC day at the moment they
            are recorded - no per-guest, per-room or per-session row is ever
            written, and attribution comes from the television&apos;s own screen
            token rather than anything in the request. Rollups are kept for{" "}
            {Math.round(data.retentionDays / 365.25 || 0) >= 1
              ? `${(data.retentionDays / 365.25).toFixed(1)} years`
              : `${data.retentionDays} days`}{" "}
            ({data.retentionDays} days) and are then deleted automatically.
          </Typography>
        </>
      )}
    </Stack>
  );
}

/**
 * Per-app attribution: how often each app is opened, and how long guests stay.
 *
 * A launch count alone cannot separate the app someone watched all evening from
 * the one they bounced straight back out of, which is exactly the distinction a
 * property needs when deciding which services to license. So the average
 * session sits next to the launch count, and the bar is scaled by TIME rather
 * than launches - the ranking by clicks is already above it.
 */
function AppsCard({ summary }: { summary: EngagementSummary }) {
  const apps = summary.apps.slice(0, 12);
  const maxSeconds = Math.max(1, ...apps.map((a) => a.seconds ?? 0));
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          Streaming apps
        </Typography>
        {apps.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No apps launched yet.
          </Typography>
        ) : (
          <Table size="small" data-testid="engagement-apps">
            <TableHead>
              <TableRow>
                <TableCell>App</TableCell>
                <TableCell align="right">Launches</TableCell>
                <TableCell align="right">Total time</TableCell>
                <TableCell align="right">Avg session</TableCell>
                <TableCell sx={{ width: "28%" }}>Share of time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apps.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell>{a.label}</TableCell>
                  <TableCell align="right">
                    {a.count.toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    {formatDuration(a.seconds)}
                  </TableCell>
                  <TableCell align="right">
                    {formatDuration(a.avgSeconds)}
                  </TableCell>
                  <TableCell>
                    <LinearProgress
                      variant="determinate"
                      value={((a.seconds ?? 0) / maxSeconds) * 100}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The content-ROI report: which promotions a property should keep running.
 *
 * "Engaged" is the honest conversion proxy a television can produce. We know a
 * promotion was on the glass and for how long; we cannot know whether a guest
 * scanned the QR, because finding out would mean identifying the guest. So the
 * report answers the question it can answer truthfully - how often the slot was
 * up long enough to act on - and names the threshold rather than dressing it up
 * as a conversion rate.
 */
function PromotionsCard({ summary }: { summary: EngagementSummary }) {
  const slots = summary.slots.slice(0, 12);
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          Promotions
        </Typography>
        {slots.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No promotions were shown in this period. Fill a promotion slot under
            Template → Promotions to start measuring.
          </Typography>
        ) : (
          <Table size="small" data-testid="engagement-slots">
            <TableHead>
              <TableRow>
                <TableCell>Promotion</TableCell>
                <TableCell align="right">Impressions</TableCell>
                <TableCell align="right">Engaged</TableCell>
                <TableCell align="right">Engaged rate</TableCell>
                <TableCell align="right">Avg time on screen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {slots.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell>
                    <Tooltip title={s.id}>
                      <span>{s.label}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    {s.count.toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    {s.engagedViews.toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    {formatPercent(s.engagedRate)}
                  </TableCell>
                  <TableCell align="right">
                    {formatDuration(s.avgSeconds)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
